import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { FC, useCallback, useState } from "react";
import styled from "styled-components";
import { ModalFunds } from "components";
import { ArrowSelect } from "icons";
import { getPercentageValue } from "lib/utils/table/getPercentageValue";
import { PortfolioTemplate } from "../types";
import { TableRowBottomLine } from "./TableRowBottomLine";

type Props = {
  className?: string;
  portfolio: PortfolioTemplate;
};

export const Table: FC<Props> = withErrorBoundary(({ className = "", portfolio }) => {
  const sortedAllocations = portfolio.allocations.sort((a, b) => b.allocation.scalar - a.allocation.scalar);
  const [openRowIndex, setOpenRowIndex] = useState<number | undefined>(undefined);

  const handleChangeOpenRowIndex = useCallback(
    (index) => () => {
      index === openRowIndex ? setOpenRowIndex(undefined) : setOpenRowIndex(index);
    },
    [openRowIndex]
  );

  const [isModal, setIsModal] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModal(false);
  }, []);

  return (
    <StyledTable className={cn(className, {})}>
      <div className="Table__header Font_12_16_450">
        <div className="Table__YearsReturnRow">
          <div className="Table__arrowColumn" />
          <div className="Table__assetClassColumn">Asset Class</div>
          <div className="Table__YearsReturnColumn Table__YearsReturnLabel Table__YearsReturnAllocation">
            Allocation
          </div>
          <div className="Table__YearsReturnColumn Table__YearsReturnLabel">1yr Return</div>
          <div className="Table__YearsReturnColumn Table__YearsReturnLabel">5yr Return</div>
          <div className="Table__YearsReturnColumn Table__YearsReturnLabel">10yr Return</div>
          <div className="Table__YearsReturnColumn Table__YearsReturnLabel">YTD Return</div>
        </div>
      </div>
      <div className="Table__body">
        {sortedAllocations.map((item, index) => {
          const { allocation, fund, fund_id, id } = item;

          return (
            <button
              className={cn("Table__row", {
                Table__openRow: index === openRowIndex,
              })}
              type="button"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleChangeOpenRowIndex(index);
                }
              }}
              onClick={handleChangeOpenRowIndex(index)}
              key={`${fund_id}-${id}`}
            >
              <div className="Table__YearsReturnRow Font_14_20_500">
                <div className="Table__arrowColumn">
                  <ArrowSelect className="Table__arrowIcon" />
                </div>
                <div className="Table__assetClassColumn">{fund.fund_type}</div>
                <div className="Table__YearsReturnColumn Table__YearsReturnAllocation">
                  {getPercentageValue(allocation.scalar * 100)}
                </div>
                <div className="Table__YearsReturnColumn">{getPercentageValue(fund.one_year_return.scalar * 100)}</div>
                <div className="Table__YearsReturnColumn">{getPercentageValue(fund.five_year_return.scalar * 100)}</div>
                <div className="Table__YearsReturnColumn">{getPercentageValue(fund.ten_year_return?.scalar * 100)}</div>
                <div className="Table__YearsReturnColumn">{getPercentageValue(fund.ytd_return?.scalar * 100)}</div>
              </div>

              <TableRowBottomLine
                className="Table__rowBottomLineContainer"
                isOpen={index === openRowIndex}
                item={item}
                onOpenModal={handleOpenModal}
              />
            </button>
          );
        })}
      </div>
      <ModalFunds
        isOpen={isModal}
        onRequestClose={handleCloseModal}
        fund={sortedAllocations[openRowIndex ?? 0]?.fund}
      />
    </StyledTable>
  );
}, {});

const StyledTable = styled.div`
  .Table__header {
    display: flex;
    font-weight: 600;
    font-size: 12px;
    padding: 12px 0;
    width: 100%;
  }

  .Table__YearsReturnRow {
    display: grid;
    grid-template-columns: 32px 2fr;
    grid-auto-columns: 1fr; /* Define the width of subsequent columns */
    grid-auto-flow: column; /* Arrange items in columns */
    width: 100%;
    align-items: center;
    text-align: right;
  }

  .Table__arrowColumn {
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .Table__assetClassColumn {
    text-align: left;
  }

  .Table__row {
    display: block;
    border-radius: 0;
    margin: initial;
    width: 100%;
    cursor: pointer;
    color: inherit;
    border-top: 1px solid ${({ theme }) => theme.colors.fog["20"]};
    padding: 12px 0;
  }

  .Table__row:first-child {
    border-color: ${({ theme }) => theme.colors.fog["30"]};
  }

  .Table__YearsReturnColumn {
    position: relative;
  }

  .Table__YearsReturnColumn:empty::before {
    content: "";
    display: inline-block;
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 16px;
    height: 1px;
    background: ${({ theme }) => theme.colors.fog["80"]};
  }

  .Table__arrowIcon {
    width: 16px;
    height: 16px;
    transition: 0.2s;

    g path {
      fill: ${({ theme }) => theme.colors.fog["80"]};
    }
  }

  .Table__openRow {
    .Table__arrowIcon {
      transform: rotate(-180deg);
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Table__assetClassColumn {
      word-break: break-word;
    }

    .Table__YearsReturnRow {
      grid-template-columns: 24px 2fr;
    }

    .Table__YearsReturnLabel {
      word-break: break-word;
      font-weight: 600;
      font-size: 12px;
    }

    .Table__YearsReturnRow > .Table__YearsReturnAllocation {
      display: none;
    }

    .Table__arrowIcon {
      width: 12px;
      height: 12px;
    }

    .Table__YearsReturnColumn:empty::before {
      margin-right: 2px;
    }
  }
`;
